import { useEffect, useReducer, useState } from 'react';
import { ApiError, getInternalTenant, listMyGrants, listTenants } from './api/client';
import { FALLBACK_PROVIDERS, fetchProviders } from './auth/providers';
import type { IdentityProviderInfo } from './auth/providers';
import { beginLogin, clearSession, completeLogin, getSession } from './auth/session';
import type { Session } from './auth/session';
import { appReducer, initialState, isAdmin } from './state/appReducer';
import type { Tab } from './state/appReducer';
import { AdminsPanel } from './AdminsPanel';
import { OfferingsPanel } from './OfferingsPanel';
import { PaymentConfigPanel } from './PaymentConfigPanel';
import { SsoConfigPanel } from './SsoConfigPanel';
import { UsersPanel } from './UsersPanel';
import { TenantsPanel } from './TenantsPanel';

/**
 * Tilgong Manager — the admin UI. Identity is a real platform sign-in (ADR-017): one
 * "Sign in with …" button per identity provider the auth server advertises (Google always;
 * Facebook/Microsoft per environment — ADR-018), each running the code+PKCE flow against
 * the auth server; the access token rides every /api call. A grant on the internal tenant
 * (resolved server-side) unlocks the all-tenants admin surface, and the admin acts as any
 * tenant from the Tenants tab; an account holding only tenant grants (GET /api/my-grants)
 * gets the same tenant-scoped view (Offerings/Admins/SSO/Payment) for its own tenants — with a
 * picker when it administers several. This file is the composition shell
 * (unit-coverage-excluded like main.tsx) — flows are covered by the Playwright E2E layer;
 * the logic units live in src/auth, src/api, and src/state.
 */
export function App() {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [session, setSessionState] = useState<Session | null>(getSession());
  const [completing, setCompleting] = useState(window.location.pathname === '/auth/callback');
  const [noAccess, setNoAccess] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [providers, setProviders] = useState<readonly IdentityProviderInfo[]>(FALLBACK_PROVIDERS);

  // The sign-in options follow the auth server's enabled-provider catalog (ADR-018).
  useEffect(() => {
    if (session !== null) return;
    void fetchProviders().then(setProviders);
  }, [session]);

  // Finish the redirect leg when the auth server sends the browser back.
  useEffect(() => {
    if (!completing) return;
    void (async () => {
      try {
        const established = await completeLogin(new URLSearchParams(window.location.search));
        window.history.replaceState({}, '', '/');
        setSessionState(established);
      } catch (e) {
        setAuthError(e instanceof Error ? e.message : String(e));
      } finally {
        setCompleting(false);
      }
    })();
  }, [completing]);

  // Once signed in, resolve the internal tenant and probe the global-admin surface: the
  // backend decides (tenant-admins grants); listTenants succeeding IS the admin signal. A 403
  // falls back to the account's own grants (GET /api/my-grants) — the tenant-admin path.
  useEffect(() => {
    if (session === null) return;
    setNoAccess(null);
    void (async () => {
      try {
        const internal = await getInternalTenant();
        dispatch({ type: 'setInternalTenant', tenantId: internal.tenantId });
        try {
          await listTenants();
          dispatch({ type: 'actAs', tenantId: internal.tenantId });
          return;
        } catch (e) {
          if (!(e instanceof ApiError && e.status === 403)) throw e;
        }
        const grants = await listMyGrants();
        if (grants.length === 0) {
          setNoAccess('This account has no admin grant. Ask an owner to add your email.');
          return;
        }
        dispatch({ type: 'setGrants', grants });
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) {
          setNoAccess('This account has no admin grant. Ask an owner to add your email.');
        } else {
          setNoAccess(e instanceof Error ? e.message : String(e));
        }
      }
    })();
  }, [session]);

  function signOut(): void {
    clearSession();
    setSessionState(null);
    setNoAccess(null);
    dispatch({ type: 'signOut' });
  }

  function signIn(idp: string): void {
    setAuthError(null);
    beginLogin(idp).catch((e: unknown) => setAuthError(e instanceof Error ? e.message : String(e)));
  }

  const admin = isAdmin(state);

  const tabButton = (tab: Tab, label: string) => (
    <button
      onClick={() => dispatch({ type: 'selectTab', tab })}
      disabled={state.tab === tab}
      style={{ fontWeight: state.tab === tab ? 'bold' : 'normal' }}
    >
      {label}
    </button>
  );

  return (
    <main style={{ fontFamily: 'system-ui, sans-serif', maxWidth: 820, margin: '2rem auto', padding: '0 1rem' }}>
      <h1>Tilgong manager</h1>

      {authError !== null && (
        <p role="alert" style={{ color: '#b00020' }}>
          {authError}
        </p>
      )}

      {completing ? (
        <p>Completing sign-in…</p>
      ) : session === null ? (
        <>
          <p>Sign in to manage tenants, products, and periods.</p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {providers.map((provider) => (
              <button key={provider.id} onClick={() => signIn(provider.id)}>
                Sign in with {provider.displayName}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <div
            style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', borderBottom: '1px solid #ddd', paddingBottom: '0.75rem' }}
          >
            <span>
              Signed in as <strong>{session.email ?? 'unknown'}</strong>
              {state.actingAs !== null && (
                <>
                  {' '}
                  — acting as <strong>{state.actingAs}</strong>
                  {admin ? ' (internal/admin)' : ''}
                </>
              )}
            </span>
            {state.grants !== null && state.grants.length > 1 && state.actingAs !== null && (
              <button type="button" onClick={() => dispatch({ type: 'switchTenant' })}>
                Switch tenant
              </button>
            )}
            <button type="button" onClick={signOut}>
              Sign out
            </button>
          </div>

          {noAccess !== null ? (
            <p role="alert" style={{ marginTop: '1rem' }}>
              {noAccess}
            </p>
          ) : state.actingAs === null && state.grants !== null ? (
            <section>
              <h2>Your tenants</h2>
              <p>Select the tenant to manage.</p>
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {state.grants.map((grant) => (
                  <li key={grant.tenantId}>
                    <button onClick={() => dispatch({ type: 'selectTenant', tenantId: grant.tenantId })}>
                      {grant.displayName}
                    </button>{' '}
                    ({grant.role})
                  </li>
                ))}
              </ul>
            </section>
          ) : state.actingAs === null ? (
            <p>Loading your access…</p>
          ) : (
            <>
              <nav style={{ display: 'flex', gap: '0.5rem', margin: '1rem 0' }}>
                {admin && tabButton('tenants', 'Tenants')}
                {tabButton('offerings', 'Offerings')}
                {tabButton('admins', 'Admins')}
                {tabButton('sso', 'SSO')}
                {tabButton('payment', 'Payment')}
                {tabButton('users', 'Users')}
              </nav>

              {admin && state.tab === 'tenants' ? (
                <TenantsPanel onManage={(tenantId) => dispatch({ type: 'selectTenant', tenantId })} />
              ) : state.tab === 'admins' ? (
                <AdminsPanel tenant={state.actingAs} />
              ) : state.tab === 'sso' ? (
                <SsoConfigPanel tenant={state.actingAs} />
              ) : state.tab === 'payment' ? (
                <PaymentConfigPanel tenant={state.actingAs} />
              ) : state.tab === 'users' ? (
                <UsersPanel tenant={state.actingAs} />
              ) : (
                <OfferingsPanel tenant={state.actingAs} />
              )}
            </>
          )}
        </>
      )}
    </main>
  );
}
